import { ProviderStatus, useOpenFeatureClientStatus } from '@openfeature/react-sdk';
import { isAxiosError } from 'axios';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { ENV } from '@/lib/config';
import { getAgentStudioPermissions, PlatformApi } from '@/lib/api/platform/platformSdk';
import { redirectToPlatformLoginPageWithReturn } from '@/lib/api/platform/redirect.utils';
import { AuthenticatedUserResponseDto } from '@monksflow/platform-api-sdk';
import type { AuthActions, AuthState, AuthSetter } from './auth.store.types';
import {
  ANY_KB_MANAGE_PERMISSIONS,
  ANY_KB_VIEW_PERMISSIONS,
  CONTENT_STUDIO_PERMISSION_RULES,
  hasAnyPermission,
  hasToolOpenPermission,
  KNOWLEDGE_HUB_PERMISSION_RULES,
  PERMISSIONS,
  USER_AGENTS_PERMISSION_RULES,
} from '@inference.engine/permissions/web';
import { logger } from '@/lib/utils/logger';
// Bridge into entities/user. See call site in signIn() below.
// Now co-located in the same slice (`entities/user`) — relative import.
import { setCurrentUser } from '../internal/bridge';
import { useWorkspaceGrantCheck } from './scoped-permissions.store';

// One deadline covers every attempt and every wait between them, because bootstrap blocks on
// all of it.
const AGENT_STUDIO_PERMISSIONS_DEADLINE_MS = 5000;
const AGENT_STUDIO_PERMISSIONS_FIRST_RETRY_DELAY_MS = 500;

// Only a received 5xx is retried. 401s go through the refresh interceptor, and a 4xx or a
// malformed body would fail the same way again. An error with no response (timeout,
// network, CORS, cancel) is not retried either: it tends to repeat, and bootstrap waits on
// every attempt.
function isTransient(error: unknown) {
  return isAxiosError(error) && (error.response?.status ?? 0) >= 500;
}

async function fetchAgentStudioPermissions(
  deadline = Date.now() + AGENT_STUDIO_PERMISSIONS_DEADLINE_MS,
  delay = AGENT_STUDIO_PERMISSIONS_FIRST_RETRY_DELAY_MS,
): Promise<string[]> {
  try {
    // A late timer can leave no time at all, and XHR reads a timeout of 0 as none and a negative
    // one as weeks. 1ms fails at once with a timeout, which is not retried.
    return await getAgentStudioPermissions(Math.max(1, deadline - Date.now()));
  } catch (error) {
    if (!isTransient(error) || Date.now() + delay >= deadline) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
    return fetchAgentStudioPermissions(deadline, delay * 2);
  }
}

interface SignInResult {
  user: unknown;
  agentStudioPermissions: string[];
}

function makeSignInStrategy(): () => Promise<SignInResult> {
  if (ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION) {
    return function mockAuth() {
      return Promise.resolve({ user: ENV.LOCAL_USER, agentStudioPermissions: [] });
    };
  }

  return async function ssoAuth() {
    try {
      const [response, agentStudioPermissions] = await Promise.all([
        PlatformApi.getAuthenticatedUser(),
        fetchAgentStudioPermissions().catch((error: unknown) => {
          // Keep signing in on /auth/me's list rather than bouncing a signed-in user to login.
          logger.warn('Agent Studio permissions fetch failed; using /auth/me only:', error);
          return [];
        }),
      ]);

      return { user: response.data, agentStudioPermissions };
    } catch (error) {
      // Redirect to login if authentication fails
      redirectToPlatformLoginPageWithReturn();
      throw error;
    }
  };
}

// Validate user data has all required fields
function validateUserData(user: unknown): user is AuthenticatedUserResponseDto {
  if (!user || typeof user !== 'object') {
    return false;
  }

  const userObj = user as Record<string, unknown>;

  // Check if all required fields exist and have the correct type
  if (
    typeof userObj.id !== 'string' ||
    typeof userObj.name !== 'string' ||
    typeof userObj.email !== 'string' ||
    !Array.isArray(userObj.workspaces) ||
    !userObj.organization ||
    typeof userObj.organization !== 'object'
  ) {
    logger.warn('Invalid user data structure:', userObj);
    return false;
  }

  const organization = userObj.organization as { id: string; name: string };
  if (!organization.id || !organization.name) {
    logger.warn('Invalid organization data structure:', organization);
    return false;
  }

  return true;
}

async function signIn(set: AuthSetter) {
  try {
    set({
      signedInUser: { error: null, fetching: true, value: null },
    });

    const strategy = makeSignInStrategy();
    const { user, agentStudioPermissions } = await strategy();

    if (!validateUserData(user)) {
      throw new Error('Invalid or missing user data');
    }

    // validateUserData does not check `permissions`, so it may be absent or malformed here.
    const tokenPermissions: unknown = user.permissions;
    const merged = [
      ...(Array.isArray(tokenPermissions)
        ? tokenPermissions.filter((key): key is string => typeof key === 'string')
        : []),
      ...agentStudioPermissions,
    ];
    const signedInUser = {
      ...user,
      permissions: [...new Set(merged)],
    };

    set({ signedInUser: { error: null, fetching: false, value: signedInUser } });
    // Bridge: push the resolved user into the canonical
    // `entities/user` store so any layer downstream can read it without
    // depending on `app/store/auth`. Single writer, multiple readers.
    setCurrentUser(signedInUser);
  } catch (error) {
    logger.error('Authentication failed:', error);
    set({
      signedInUser: {
        // eslint-disable-next-line i18next/no-literal-string -- developer diagnostic held in `authStore.error`; the login flow redirects rather than rendering it
        error: error instanceof Error ? error.message : 'Authentication failed',
        fetching: false,
        value: null,
      },
    });
    // Keep both stores consistent on failure: if a previous
    // sign-in left a stale user in the canonical user store, clear it so
    // downstream readers don't see a phantom user after re-auth fails.
    setCurrentUser(null);
  }
}

export const useAuth = create<AuthState & AuthActions>((set, get) => ({
  signedInUser: {
    error: null,
    fetching: false,
    value: null,
  },
  isFetching: () => get().signedInUser.fetching,
  isSignedIn: () => !get().signedInUser.fetching && !!get().signedInUser.value,
  signIn: () => signIn(set),
}));

// Auth Store Selectors - Granular for optimal re-rendering.
//
// `useCurrentUserId` and `useUserWorkspaces` moved to
// `entities/user` (the canonical home for signed-in user data). Auth retains
// only the signIn lifecycle (`isSignedIn`, `isFetching`, `signIn`) and
// `usePermissions`, which derives from the same auth-bootstrapped data.

/**
 * True while the flag provider re-evaluates (workspace switch). Permissions that depend on a
 * flag are not final until it settles, so a route guard must not redirect on them yet.
 */
export const usePermissionsReconciling = () =>
  useOpenFeatureClientStatus() === ProviderStatus.RECONCILING;

export const usePermissions = () => {
  const allowWorkspace = useWorkspaceGrantCheck();

  return useAuth(
    useShallow((state) => {
      const perms = state.signedInUser.value?.permissions ?? [];
      const check = (rules: readonly string[]) => hasAnyPermission(perms, rules);
      const isInstanceAdmin = perms.includes(PERMISSIONS.InstanceAdmin);
      const isOrganizationAdmin = isInstanceAdmin || perms.includes(PERMISSIONS.OrganizationAdmin);
      const isWorkspaceAdmin = isOrganizationAdmin || perms.includes(PERMISSIONS.WorkspaceAdmin);

      const rules = KNOWLEDGE_HUB_PERMISSION_RULES;
      const studioRules = CONTENT_STUDIO_PERMISSION_RULES;

      return {
        // General
        isInstanceAdmin,
        isOrganizationAdmin,
        isWorkspaceAdmin,

        // Knowledge Bases
        canViewFoundationalKnowledgeBases: check(rules.foundationalKnowledgeBases.view),
        canManageFoundationalKnowledgeBases: check(rules.foundationalKnowledgeBases.manage),

        canViewOrganizationKnowledgeBases: check(rules.organizationKnowledgeBases.view),
        canManageOrganizationKnowledgeBases: check(rules.organizationKnowledgeBases.manage),

        canViewWorkspaceKnowledgeBases: check(rules.workspaceKnowledgeBases.view),
        canManageWorkspaceKnowledgeBases: check(rules.workspaceKnowledgeBases.manage),

        canViewAnyKnowledgeBase: check(ANY_KB_VIEW_PERMISSIONS),
        canManageAnyKnowledgeBase: check(ANY_KB_MANAGE_PERMISSIONS),

        canViewBriefTemplates: check(rules.briefTemplates.view),
        canManageBriefTemplates: check(rules.briefTemplates.manage),
        canViewWorkspaceBriefTemplates: check(rules.workspaceBriefTemplates.view),
        canManageWorkspaceBriefTemplates: check(rules.workspaceBriefTemplates.manage),
        canViewOrganizationBriefTemplates: check(rules.organizationBriefTemplates.view),
        canManageOrganizationBriefTemplates: check(rules.organizationBriefTemplates.manage),
        canViewSystemBriefTemplates: check(rules.systemBriefTemplates.view),
        canManageSystemBriefTemplates: check(rules.systemBriefTemplates.manage),

        canViewTaxonomy: check(rules.taxonomy.view),
        canManageTaxonomy: check(rules.taxonomy.manage),

        // `use` = open/use the templates agent (read-only). True for both the
        // dedicated use permission and the manage permission. `manage` = the
        // config screen + write, gated separately below.
        canUseEmailTemplates: check(rules.emailTemplates.view),
        canManageEmailTemplates: check(rules.emailTemplates.manage),

        // Brand & Visual Guidelines
        canViewBrandVisualGuidelines: check(rules.brandVisualGuidelines.view),
        canManageBrandVisualGuidelines: check(rules.brandVisualGuidelines.manage),

        // Brand Assets
        // Brand Assets and Content Studio read the workspace scope, the one the backend decides on.
        canOpenBrandAssets: allowWorkspace(rules.brandAssets.open),
        canUploadBrandAssets: allowWorkspace(rules.brandAssets.upload),
        canManageBrandAssets: allowWorkspace(rules.brandAssets.manage),

        canViewContentStudio: allowWorkspace(studioRules.view),
        canCreateAdsets: allowWorkspace(studioRules.createAdsets),
        canEditAdsets: allowWorkspace(studioRules.editAdsets),
        canDeleteAdsets: allowWorkspace(studioRules.deleteAdsets),
        canCreateRuns: allowWorkspace(studioRules.createRuns),
        canExportRuns: allowWorkspace(studioRules.exportRuns),

        // Audiences
        canViewAudiences: check(rules.audiences.view),
        canManageAudiences: check(rules.audiences.manage),

        //Content Test
        canAccessTestContent: check([PERMISSIONS.ToolTestContentOpen]),

        // Focus Groups
        canAccessFocusGroups: check([PERMISSIONS.ToolFocusGroupsOpen]),

        // Platform
        canManageTools: check([PERMISSIONS.PlatformManageTools]),
        canManageAdFormats: check([
          PERMISSIONS.ContentStudioManageAdFormats,
          PERMISSIONS.InstanceAdmin,
        ]),
        canViewCostDashboard: check([PERMISSIONS.PlatformViewCostDashboard]),
        canManageCredentials: check([PERMISSIONS.PlatformManageCredentials]),
        canManageModelsWhitelisting: check([PERMISSIONS.PlatformManageModelsWhitelisting]),
        canManageUserAgents: check(USER_AGENTS_PERMISSION_RULES.manage),
        canViewUserAgents: check(USER_AGENTS_PERMISSION_RULES.view),

        // Tools
        canOpenTool: (agentId: string) => hasToolOpenPermission(perms, agentId),

        // Aggregate: can access Knowledge Hub section. Every section that lives
        // under /knowledge-hub has to appear here — the route guard for the whole
        // subtree renders a redirect INSTEAD of the outlet, so a section missing
        // from this disjunct is unreachable no matter what its own guard says.
        canAccessKnowledgeHub:
          check(ANY_KB_VIEW_PERMISSIONS) ||
          check(rules.audiences.view) ||
          check(rules.brandVisualGuidelines.view) ||
          check(rules.taxonomy.view) ||
          allowWorkspace(rules.brandAssets.open),
        // Aggregate: can access Studio Settings. Same rule as the hub above,
        // for the same reason — the sidebar offers one entry pointing at
        // /admin, so a page whose own guard would admit someone missing from
        // this disjunct is reachable only by typing its URL. Every gate used
        // by an /admin child has to appear here.
        //
        // Deliberately blind to the feature flags on some of those children:
        // a flag is a rollout state, not a permission, and the flag reads
        // belong where a component can subscribe to them. AdminIndexRedirect
        // is what keeps a flagged-off destination from being landed on.
        canViewStudioSettings:
          check([PERMISSIONS.PlatformManageTools, PERMISSIONS.PlatformManageModelsWhitelisting]) ||
          check(USER_AGENTS_PERMISSION_RULES.manage) ||
          check([PERMISSIONS.ContentStudioManageAdFormats]) ||
          isWorkspaceAdmin,

        // IQ
        canAccessIq: check([PERMISSIONS.ToolIqOpen]),

        // Resources
        canAccessResources: check([PERMISSIONS.ToolResourcesOpen]),

        // Integrations
        canAccessGwiIntegration: check([PERMISSIONS.IntegrationGwiAccess]),
        // Licensed research providers IQ reads. One key each covers activating the source in
        // Knowledge and reading the report sections built from it.
        canAccessYougov: check([PERMISSIONS.IntegrationYougovAccess]),
        canAccessPathmatics: check([PERMISSIONS.IntegrationPathmaticsAccess]),
        canAccessStatistaIntegration: check([PERMISSIONS.IntegrationStatistaAccess]),

        // Support
        canAccessSupport: check([PERMISSIONS.PlatformAccessSupport]),
      };
    }),
  );
};

export type Permissions = ReturnType<typeof usePermissions>;
